import React, { useState } from 'react';
import { Box, PalletDimensions } from '../types';
import { BoxInput } from './BoxInput';
import { PalletDimensionsInput } from './PalletDimensionsInput';
import { sampleBoxes } from '../utils/sampleData';
import { testOptimizer } from '../utils/testOptimizer';

interface PreOptimizationPageProps {
  boxes: Box[];
  onAddBox: (box: Box) => void;
  onAddBoxes: (boxes: Box[]) => void;
  onRemoveBox: (index: number) => void;
  onCalculate: (palletDimensions: PalletDimensions) => void;
  onClear: () => void;
  onLoadSample: () => void;
}

export const PreOptimizationPage: React.FC<PreOptimizationPageProps> = ({
  boxes,
  onAddBox,
  onAddBoxes,
  onRemoveBox,
  onCalculate,
  onClear,
  onLoadSample
}) => {
  const [palletDimensions, setPalletDimensions] = useState<PalletDimensions>({
    length: 120,
    width: 80,
    maxHeight: 170
  });
  const [showTestOptimizer, setShowTestOptimizer] = useState(false);

  const handleTestOptimizer = () => {
    testOptimizer();
  };

  const handleLoadSample = () => {
    onLoadSample();
    setShowTestOptimizer(true);
  };

  const handleCalculate = () => {
    onCalculate(palletDimensions);
  };

  const handleClear = () => {
    onClear();
    setShowTestOptimizer(false);
  };

  return (
    <div>
      {/* Hero Section */}
      <div className="text-center mb-8 sm:mb-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
          Pallet Builder
        </h1>
        <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
          Advanced FCL optimization tool for efficient pallet stacking and shipping logistics. 
          Upload CSV files, track product IDs, and visualize your optimized pallets.
        </p>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 sm:gap-8">
        <div className="xl:col-span-1 order-2 xl:order-1">
          {/* Pallet Dimensions Input */}
          <PalletDimensionsInput 
            dimensions={palletDimensions}
            onDimensionsChange={setPalletDimensions}
          />
          
          <BoxInput onAddBox={onAddBox} onAddBoxes={onAddBoxes} palletDimensions={palletDimensions} />
          
          {boxes.length > 0 && (
            <div className="mt-6 sm:mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Added Boxes</h2>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {boxes.map((box, index) => (
                  <div key={box.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-700 text-sm">
                      Box {index + 1}: {box.length}×{box.width}×{box.height} cm (Qty: {box.quantity})
                      {box.asin && <span className="text-blue-600 ml-2 font-medium">Product ID: {box.asin}</span>}
                    </span>
                    <button
                      onClick={() => onRemoveBox(index)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={handleCalculate}
                  className="bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Calculate Optimization
                </button>
                <button
                  onClick={handleClear}
                  className="bg-gray-600 text-white py-3 px-6 rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          {boxes.length === 0 && (
            <div className="mt-6 sm:mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 text-center">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Quick Start</h2>
              <p className="text-gray-600 mb-6">
                Add box types above, upload a CSV file, or load sample data to test the optimization.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleLoadSample}
                  className="bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Load Sample Data
                </button>
                {showTestOptimizer && (
                  <button
                    onClick={handleTestOptimizer}
                    className="bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    Test Optimizer
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="xl:col-span-2 order-1 xl:order-2">
          <div className="sticky top-16 xl:top-32 bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Ready to Optimize</h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto px-4">
                Add your box types and quantities to get started with pallet optimization. 
                The system will calculate the most efficient arrangement for your FCL shipment.
              </p>
              
              {boxes.length > 0 ? (
                <div className="bg-blue-50 rounded-lg p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-blue-900 mb-2">
                    {boxes.length} Box Type{boxes.length !== 1 ? 's' : ''} Added
                  </h3>
                  <p className="text-blue-700 text-sm mb-4">
                    Total boxes: {boxes.reduce((sum, box) => sum + box.quantity, 0)}
                  </p>
                  <p className="text-blue-700 text-sm mb-4">
                    Pallet size: {palletDimensions.length} × {palletDimensions.width} × {palletDimensions.maxHeight} cm
                  </p>
                  <button
                    onClick={handleCalculate}
                    className="bg-blue-600 text-white py-3 px-6 sm:px-8 rounded-lg hover:bg-blue-700 transition-colors font-medium text-base sm:text-lg"
                  >
                    Start Optimization
                  </button>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">No Boxes Added</h3>
                  <p className="text-gray-600 text-sm">
                    Add box types to begin optimization
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 